import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/sonner';
import ChatbotEllie from '@/components/ChatbotEllie';
import { RequireRole, PublicOrPortal } from '@/components/auth/RequireRole';
import OrgCoursesPage from '@/pages/org/OrgCoursesPage';

// In the course player keep toasts at the top and hide the chatbot so the bottom nav stays clear
const PlayerAwareOverlays = () => {
  const inPlayer = useLocation().pathname.startsWith('/learn/');
  return (
    <>
      <Toaster position={inPlayer ? 'top-center' : 'bottom-right'} />
      {!inPlayer && <ChatbotEllie />}
    </>
  );
};
import CookieConsentBanner from '@/components/CookieConsentBanner';
import { PwaInstallPrompt, OfflineIndicator, UpdateBanner } from '@/components/PwaComponents';
import { AuthProvider } from '@/contexts/AuthContext';
import { CmsProvider } from '@/contexts/CmsContext';
import { TextSizeProvider } from '@/contexts/TextSizeContext';

// Pages
import HomePage from '@/pages/HomePage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import CoursesPage from '@/pages/CoursesPage';
import CustomPage from '@/pages/CustomPage';

// Dashboards
import LearnerDashboard from '@/pages/dashboards/LearnerDashboard';
import AdminDashboard from '@/pages/dashboards/AdminDashboard';
import ContentManagerDashboard from '@/pages/dashboards/ContentManagerDashboard';
import OrgAdminDashboard from '@/pages/dashboards/OrgAdminDashboard';

// Course Builder
import CourseBuilder from '@/pages/builder/CourseBuilder';

// Course Player
import CoursePlayer from '@/pages/player/CoursePlayer';
import CoursePreviewPage from '@/pages/player/CoursePreviewPage';

// Course Detail
import CourseDetailPage from '@/pages/CourseDetailPage';

// CMS Admin
import CmsPages from '@/pages/admin/CmsPages';
import CmsPageEditor from '@/pages/admin/CmsPageEditor';
import NavigationManager from '@/pages/admin/NavigationManager';
import OrganisationManagement from '@/pages/admin/OrganisationManagement';
import SubscriptionTierManagement from '@/pages/admin/SubscriptionTierManagement';
import OnboardingSettings from '@/pages/admin/OnboardingSettings';
import HomepageEditor from '@/pages/admin/HomepageEditor';
import AdminUsersPage from '@/pages/admin/AdminUsersPage';
import BrandingPage from '@/pages/admin/BrandingPage';
import MediaLibraryPage from '@/pages/admin/MediaLibraryPage';
import CertificateTemplateManager from '@/pages/admin/CertificateTemplateManager';
import QuestionBankPage from '@/pages/admin/QuestionBankPage';
import CourseManagementPage from '@/pages/admin/CourseManagementPage';
import SystemReportsPage from '@/pages/admin/SystemReportsPage';
import SystemSettingsPage from '@/pages/admin/SystemSettingsPage';
import AdminEnquiriesPage from '@/pages/admin/AdminEnquiriesPage';
import AdminBundlesPage from '@/pages/admin/AdminBundlesPage';
import AdminCategoriesPage from '@/pages/admin/AdminCategoriesPage';
import PromoCodesPage from '@/pages/admin/PromoCodesPage';

// Organisation
import TeamManagement from '@/pages/org/TeamManagement';
import SubscriptionPage from '@/pages/org/SubscriptionPage';
import OrgReporting from '@/pages/org/OrgReporting';

// Certificates
import MyCertificatesPage from '@/pages/MyCertificatesPage';
import CertificateVerifyPage from '@/pages/CertificateVerifyPage';
import AdminCertificates from '@/pages/admin/AdminCertificates';

// Pricing
import PricingPage from '@/pages/PricingPage';

// Passport & Employer Verification
import { MyPassport, SharedPassport } from '@/pages/PassportPage';
import EmployerVerifyPage from '@/pages/EmployerVerifyPage';

// Data Management
import AdminDataPage from '@/pages/admin/AdminDataPage';

// Accept Invite
import AcceptInvitePage from '@/pages/AcceptInvitePage';
import { ForgotPasswordPage, ResetPasswordPage } from '@/pages/PasswordResetPages';
import AdminApprovalsPage from '@/pages/admin/AdminApprovalsPage';
import KnowledgeHubPage from '@/pages/hub/KnowledgeHubPage';
import ArticlePage from '@/pages/hub/ArticlePage';
import EbookAccessPage from '@/pages/hub/EbookAccessPage';
import AdminHubPage from '@/pages/admin/AdminHubPage';
import { BundlePurchasePage, PaymentReturnPage } from '@/pages/BundlePurchasePage';
import AccessibilityPage from '@/pages/AccessibilityPage';
import LegalPage from '@/pages/LegalPage';
import TempAdminLogin from '@/pages/TempAdminLogin';

// Compliance & GDPR
import TranscriptPage from '@/pages/TranscriptPage';
import ComplianceDashboard from '@/pages/admin/ComplianceDashboard';

// About & Contact
import ProfilePage from '@/pages/account/ProfilePage';
import MyCoursesPage from '@/pages/account/MyCoursesPage';
import OrganisationSettingsPage from '@/pages/account/OrganisationSettingsPage';
import PendingApprovalPage from '@/pages/account/PendingApprovalPage';
import AdminPaymentsPage from '@/pages/admin/AdminPaymentsPage';
import ContactPage from '@/pages/ContactPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import TermsOfServicePage from '@/pages/TermsOfServicePage';
import CookiesPolicyPage from '@/pages/CookiesPolicyPage';


function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <TextSizeProvider>
      <CmsProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<PublicOrPortal><HomePage /></PublicOrPortal>} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/courses" element={<PublicOrPortal><CoursesPage /></PublicOrPortal>} />
            <Route path="/courses/:id" element={<CourseDetailPage />} />
            <Route path="/course/:id" element={<CourseDetailPage />} />
            <Route path="/page/for-employers" element={<Navigate to="/for-organisations" replace />} />
            <Route path="/page/for-organisations" element={<Navigate to="/for-organisations" replace />} />
            <Route path="/page/about-us" element={<Navigate to="/about" replace />} />
            <Route path="/page/:slug" element={<CustomPage />} />
            <Route path="/for-organisations" element={<CustomPage slug="for-organisations" />} />
            <Route path="/about" element={<PublicOrPortal><CustomPage slug="about-us" /></PublicOrPortal>} />
            <Route path="/contact" element={<PublicOrPortal><ContactPage /></PublicOrPortal>} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/cookies-policy" element={<CookiesPolicyPage />} />
            <Route path="/verify/:certNumber" element={<CertificateVerifyPage />} />
            <Route path="/certificates/verify" element={<CertificateVerifyPage />} />
            <Route path="/verify/employer" element={<EmployerVerifyPage />} />
            <Route path="/passport/:passportId" element={<SharedPassport />} />
            <Route path="/pending-approval" element={<PendingApprovalPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/knowledge-hub" element={<KnowledgeHubPage />} />
            <Route path="/knowledge-hub/purchase/success" element={<PaymentReturnPage kind="ebook" />} />
            <Route path="/knowledge-hub/ebook/:token" element={<EbookAccessPage />} />
            <Route path="/knowledge-hub/:slug" element={<ArticlePage />} />
            <Route path="/bundles/purchase/success" element={<PaymentReturnPage kind="bundle" />} />
            <Route path="/bundles/:bundleId" element={<BundlePurchasePage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accessibility" element={<AccessibilityPage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/temp-admin" element={<TempAdminLogin />} />
            <Route path="/pricing" element={<PublicOrPortal><PricingPage /></PublicOrPortal>} />

            {/* Learner Routes */}
            <Route path="/dashboard" element={<LearnerDashboard />} />
            <Route path="/my-courses" element={<MyCoursesPage />} />
            <Route path="/learn/:courseId" element={<CoursePlayer />} />
            <Route path="/course/:courseId/preview" element={<CoursePreviewPage />} />
            <Route path="/certificates" element={<MyCertificatesPage />} />
            <Route path="/passport" element={<MyPassport />} />
            <Route path="/transcript" element={<TranscriptPage />} />
            <Route path="/profile" element={<ProfilePage />} />

            {/* Payment Routes */}
            <Route path="/payment/success" element={<PaymentReturnPage kind="course" />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<RequireRole roles={['super_admin']}><AdminDashboard /></RequireRole>} />
            <Route path="/admin/users" element={<RequireRole roles={['super_admin']}><AdminUsersPage /></RequireRole>} />
            <Route path="/admin/users/new" element={<RequireRole roles={['super_admin']}><Navigate to="/admin/users?add=1" replace /></RequireRole>} />
            <Route path="/admin/organisations" element={<RequireRole roles={['super_admin']}><OrganisationManagement /></RequireRole>} />
            <Route path="/admin/subscription-tiers" element={<RequireRole roles={['super_admin']}><SubscriptionTierManagement /></RequireRole>} />
            <Route path="/admin/onboarding" element={<RequireRole roles={['super_admin']}><OnboardingSettings /></RequireRole>} />
            <Route path="/admin/homepage" element={<RequireRole roles={['super_admin', 'content_manager']}><HomepageEditor /></RequireRole>} />
            <Route path="/admin/knowledge-hub" element={<RequireRole roles={['super_admin', 'content_manager']}><AdminHubPage /></RequireRole>} />
            <Route path="/admin/approvals" element={<RequireRole roles={['super_admin']}><AdminApprovalsPage /></RequireRole>} />
            <Route path="/admin/courses" element={<RequireRole roles={['super_admin', 'content_manager']}><CourseManagementPage /></RequireRole>} />
            <Route path="/admin/courses/new" element={<RequireRole roles={['super_admin', 'content_manager']}><CourseBuilder /></RequireRole>} />
            <Route path="/admin/courses/:courseId/edit" element={<RequireRole roles={['super_admin', 'content_manager']}><CourseBuilder /></RequireRole>} />
            <Route path="/admin/questions" element={<RequireRole roles={['super_admin', 'content_manager']}><QuestionBankPage /></RequireRole>} />
            <Route path="/admin/certificates" element={<RequireRole roles={['super_admin']}><AdminCertificates /></RequireRole>} />
            <Route path="/admin/certificates/template" element={<RequireRole roles={['super_admin', 'content_manager']}><CertificateTemplateManager /></RequireRole>} />
            <Route path="/admin/data" element={<RequireRole roles={['super_admin', 'content_manager']}><AdminDataPage /></RequireRole>} />
            <Route path="/admin/payments" element={<RequireRole roles={['super_admin']}><AdminPaymentsPage /></RequireRole>} />
            <Route path="/admin/reports" element={<RequireRole roles={['super_admin']}><SystemReportsPage /></RequireRole>} />
            <Route path="/admin/email" element={<RequireRole roles={['super_admin']}><Navigate to="/admin/settings" replace /></RequireRole>} />
            <Route path="/admin/branding" element={<RequireRole roles={['super_admin']}><BrandingPage /></RequireRole>} />
            <Route path="/admin/media" element={<RequireRole roles={['super_admin', 'content_manager']}><MediaLibraryPage /></RequireRole>} />
            <Route path="/admin/settings" element={<RequireRole roles={['super_admin']}><SystemSettingsPage /></RequireRole>} />
            <Route path="/admin/compliance" element={<RequireRole roles={['super_admin']}><ComplianceDashboard /></RequireRole>} />
            <Route path="/admin/enquiries" element={<RequireRole roles={['super_admin']}><AdminEnquiriesPage /></RequireRole>} />
            <Route path="/admin/bundles" element={<RequireRole roles={['super_admin']}><AdminBundlesPage /></RequireRole>} />
            <Route path="/admin/categories" element={<RequireRole roles={['super_admin', 'content_manager']}><AdminCategoriesPage /></RequireRole>} />
            <Route path="/admin/promo-codes" element={<RequireRole roles={['super_admin']}><PromoCodesPage /></RequireRole>} />

            {/* CMS Admin Routes */}
            <Route path="/admin/pages" element={<RequireRole roles={['super_admin', 'content_manager']}><CmsPages /></RequireRole>} />
            <Route path="/admin/pages/new" element={<RequireRole roles={['super_admin', 'content_manager']}><CmsPageEditor /></RequireRole>} />
            <Route path="/admin/pages/:pageId/edit" element={<RequireRole roles={['super_admin', 'content_manager']}><CmsPageEditor /></RequireRole>} />
            <Route path="/admin/navigation" element={<RequireRole roles={['super_admin']}><NavigationManager /></RequireRole>} />

            {/* Content Manager Routes */}
            <Route path="/cms" element={<RequireRole roles={['super_admin', 'content_manager']}><ContentManagerDashboard /></RequireRole>} />
            <Route path="/cms/courses" element={<RequireRole roles={['super_admin', 'content_manager']}><CourseManagementPage /></RequireRole>} />
            <Route path="/cms/courses/new" element={<RequireRole roles={['super_admin', 'content_manager']}><CourseBuilder /></RequireRole>} />
            <Route path="/cms/courses/:courseId/edit" element={<RequireRole roles={['super_admin', 'content_manager']}><CourseBuilder /></RequireRole>} />
            <Route path="/cms/questions" element={<RequireRole roles={['super_admin', 'content_manager']}><QuestionBankPage /></RequireRole>} />
            <Route path="/cms/certificates" element={<RequireRole roles={['super_admin', 'content_manager']}><CertificateTemplateManager /></RequireRole>} />
            <Route path="/cms/analytics" element={<Navigate to="/cms" replace />} />
            <Route path="/cms/media" element={<Navigate to="/admin/media" replace />} />

            {/* Organisation Admin Routes */}
            <Route path="/organisation" element={<RequireRole roles={['org_admin', 'super_admin']}><OrgAdminDashboard /></RequireRole>} />
            <Route path="/organisation/team" element={<RequireRole roles={['org_admin', 'super_admin']}><TeamManagement /></RequireRole>} />
            <Route path="/organisation/courses" element={<RequireRole roles={['org_admin']}><OrgCoursesPage /></RequireRole>} />
            <Route path="/organisation/dashboard" element={<Navigate to="/organisation" replace />} />
            <Route path="/organisation/reports" element={<Navigate to="/organisation/reporting" replace />} />
            <Route path="/organisation/subscription" element={<RequireRole roles={['org_admin', 'super_admin']}><SubscriptionPage /></RequireRole>} />
            <Route path="/organisation/reporting" element={<RequireRole roles={['org_admin', 'super_admin']}><OrgReporting /></RequireRole>} />
            <Route path="/organisation/subscription/success" element={<RequireRole roles={['org_admin', 'super_admin']}><PaymentReturnPage kind="subscription" /></RequireRole>} />
            <Route path="/organisation/settings" element={<RequireRole roles={['org_admin', 'super_admin']}><OrganisationSettingsPage /></RequireRole>} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <PlayerAwareOverlays />
          <CookieConsentBanner />
          <PwaInstallPrompt />
          <OfflineIndicator />
          <UpdateBanner />
        </BrowserRouter>
      </CmsProvider>
      </TextSizeProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
